import { refreshSyncCounts, syncPendingMissions } from './sync-queue'

import { type ApiClient } from '@/lib/api'
import { useSyncStore } from '@/stores/sync.store'

/**
 * Wires browser network events to the queue walker.
 *
 * Side-effects only — no React. Called once from `app.tsx` after
 * the auth store is hydrated, with the same `ApiClient` the app
 * uses everywhere. Returns a teardown function so a hot-reload
 * doesn't leak listeners.
 *
 * Behaviour:
 *   - On mount: reads current `navigator.onLine`, refreshes the
 *     queue counts, and kicks off a sync if we're online (catches
 *     any rows left behind by a previous session).
 *   - On `online` event: flips the store, runs a sync.
 *   - On `offline` event: flips the store. Does nothing else —
 *     the next mission submit will detect the offline state and
 *     enqueue the row directly.
 *
 * The actual `syncPendingMissions` call is fire-and-forget; the
 * walker reports its own progress through `useSyncStore`.
 */
export function installSyncController(api: ApiClient): () => void {
  const store = useSyncStore.getState()
  store.setOnline(typeof navigator === 'undefined' ? true : navigator.onLine)
  void refreshSyncCounts()
  if (typeof navigator !== 'undefined' && navigator.onLine) {
    void syncPendingMissions(api)
  }

  function onOnline(): void {
    useSyncStore.getState().setOnline(true)
    void syncPendingMissions(api)
  }
  function onOffline(): void {
    useSyncStore.getState().setOnline(false)
  }

  if (typeof window === 'undefined') {
    return () => undefined
  }
  window.addEventListener('online', onOnline)
  window.addEventListener('offline', onOffline)

  return () => {
    window.removeEventListener('online', onOnline)
    window.removeEventListener('offline', onOffline)
  }
}

/**
 * Manual trigger — wired to the "Réessayer" button in the UI and
 * to the optional pull-to-refresh on the dashboard. No-op if
 * we're already syncing or offline.
 */
export async function triggerSync(api: ApiClient): Promise<void> {
  const { isOnline, syncing } = useSyncStore.getState()
  if (!isOnline || syncing) return
  await syncPendingMissions(api)
}
